import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Pencil, Layers } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import { canManageContractors } from "@/features/contractors/utils";
import {
  useContractorQualification,
  useSaveContractorQualification,
  useScopeCategories,
  useVoivodeships,
  type QualificationStatus,
} from "@/features/rfq/api/scopes";
import { CountyDisclosure, CountyRefinement } from "./CountyRefinement";
import {
  emptiedVoivodeships,
  pruneCounties,
  selectedCounties,
  withCounties,
  type CountySelection,
} from "./counties";

/**
 * Zakres współpracy firmy na jej karcie: JAKIE kategorie prac i GDZIE.
 *
 * Nadaje się kategorie; domyślnie w całości, ale można zejść do konkretnych pozycji,
 * gdy firma robi tylko wycinek. „Cała kategoria" obejmuje też pozycje dodane w
 * przyszłości — wyliczenie nie. Zapis nadpisuje cały stan, więc formularz to po
 * prostu odbicie tego, co ma obowiązywać po zapisaniu.
 *
 * Obszar działa tak samo: województwo domyślnie w całości, a pod zaznaczonym
 * województwem czeka zwinięta lista jego powiatów do doprecyzowania (patrz
 * `counties.ts` — komplet powiatów = brak zawężenia).
 *
 * Kategorie nadaje dyrektor techniczny; sam obszar może też ustawiać osoba z prawem
 * „Podwykonawcy i umowy ramowe". Ona widzi kategorie tylko do odczytu, a zapis
 * idzie bez `categories` (backend zostawia je wtedy nietknięte).
 */

interface Props {
  contractorId: string;
}

interface PickedCategory {
  status: QualificationStatus;
  allPositions: boolean;
  positions: string[];
}

export function CooperationScopeSection({ contractorId }: Props) {
  const { user } = useAuthStore();
  // Kategorie i obszar zapisuje ten sam krąg: CEO, dyr. techniczny i uprawnienie
  // „Podwykonawcy i umowy ramowe" (backendowa bramka CanManageContractors).
  const canEdit = canManageContractors(user);

  const { data: qualification, isLoading } = useContractorQualification(contractorId);
  const { data: categories } = useScopeCategories();
  const { data: voivodeships } = useVoivodeships();
  const save = useSaveContractorQualification(contractorId);

  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<Record<string, PickedCategory>>({});
  const [areas, setAreas] = useState<string[]>([]);
  const [counties, setCounties] = useState<CountySelection>({});

  // Wejście w tryb edycji zaczyna od stanu zapisanego, nie od pustego formularza.
  useEffect(() => {
    if (!qualification) return;
    setPicked(
      Object.fromEntries(
        qualification.categories.map((c) => [
          c.category,
          {
            status: c.status,
            allPositions: c.all_positions,
            positions: c.positions.map((p) => p.id),
          },
        ]),
      ),
    );
    setAreas(qualification.voivodeships.map((v) => v.voivodeship));
    setCounties(
      Object.fromEntries(
        qualification.voivodeships
          .filter((v) => v.counties.length > 0)
          .map((v) => [v.voivodeship, v.counties]),
      ),
    );
  }, [qualification]);

  if (isLoading || !qualification) return null;

  const toggleCategory = (id: string) =>
    setPicked((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      // Domyślnie cała kategoria — zawężanie do pojedynczych pozycji to wyjątek.
      else next[id] = { status: "full", allPositions: true, positions: [] };
      return next;
    });

  const patchCategory = (id: string, patch: Partial<PickedCategory>) =>
    setPicked((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const togglePosition = (categoryId: string, positionId: string) =>
    setPicked((prev) => {
      const current = prev[categoryId];
      const positions = current.positions.includes(positionId)
        ? current.positions.filter((p) => p !== positionId)
        : [...current.positions, positionId];
      return { ...prev, [categoryId]: { ...current, positions } };
    });

  const toggleArea = (value: string) => {
    const next = areas.includes(value) ? areas.filter((v) => v !== value) : [...areas, value];
    setAreas(next);
    // Odznaczone województwo zabiera ze sobą zawężenie; zaznaczone startuje od całości.
    setCounties((prev) => pruneCounties(prev, next));
  };

  // Województwo zawężone do zera powiatów to niedokończone kliknięcie — nie zapisujemy.
  const emptied = emptiedVoivodeships(counties);

  const handleSave = () =>
    save.mutate(
      {
        categories: Object.entries(picked).map(([category, value]) => ({
          category,
          status: value.status,
          all_positions: value.allPositions,
          positions: value.allPositions ? [] : value.positions,
          note: "",
        })),
        voivodeships: areas,
        counties,
      },
      { onSuccess: () => setEditing(false) },
    );

  return (
    <section className="mb-6">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Zakres współpracy
        </h2>
        {canEdit && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <Pencil size={14} className="mr-1.5 inline" />{" "}
            Edytuj
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Anuluj
            </Button>
            <Button onClick={handleSave} disabled={save.isPending || emptied.length > 0}>
              {save.isPending ? "Zapisywanie…" : "Zapisz"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-px bg-[var(--color-border)] md:grid-cols-2">
        {/* Kategorie prac */}
        <div className="bg-white p-5 dark:bg-gray-800">
          <p className="mb-3 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            <Layers size={12} /> Kategorie prac
          </p>

          {editing ? (
            <div className="space-y-2.5">
              {(categories ?? []).map((category) => {
                const pick = picked[category.id];
                return (
                  <div key={category.id}>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!pick}
                        onChange={() => toggleCategory(category.id)}
                        className="mt-1"
                      />
                      <span className="flex-1 text-[var(--color-text-primary)]">
                        {category.name}
                      </span>
                      {pick && (
                        <select
                          value={pick.status}
                          onChange={(e) =>
                            patchCategory(category.id, {
                              status: e.target.value as QualificationStatus,
                            })
                          }
                          className="border border-[var(--color-border)] bg-transparent px-1 py-0.5 text-xs"
                        >
                          <option value="full">Pełna</option>
                          <option value="conditional">Warunkowa</option>
                        </select>
                      )}
                    </label>

                    {/* Zakres wewnątrz kategorii — całość albo wskazane pozycje. */}
                    {pick && category.scopes.length > 0 && (
                      <div className="ml-6 mt-1 border-l border-[var(--color-border)] pl-3">
                        <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                          <input
                            type="checkbox"
                            checked={pick.allPositions}
                            onChange={(e) =>
                              patchCategory(category.id, {
                                allPositions: e.target.checked,
                                positions: e.target.checked
                                  ? []
                                  : category.scopes.map((s) => s.id),
                              })
                            }
                          />
                          Cała kategoria (także pozycje dodane w przyszłości)
                        </label>
                        {!pick.allPositions && (
                          <div className="mt-1 space-y-0.5">
                            {category.scopes.map((scope) => (
                              <label
                                key={scope.id}
                                className="flex items-center gap-2 text-xs"
                              >
                                <input
                                  type="checkbox"
                                  checked={pick.positions.includes(scope.id)}
                                  onChange={() => togglePosition(category.id, scope.id)}
                                />
                                <span className="text-[var(--color-text-primary)]">
                                  {scope.name}
                                </span>
                              </label>
                            ))}
                            {pick.positions.length === 0 && (
                              <p className="text-xs text-red-600 dark:text-red-400">
                                Wskaż co najmniej jedną pozycję albo zaznacz całą kategorię.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {!pick && category.scopes.length > 0 && (
                      <p className="ml-6 text-xs text-[var(--color-text-muted)]">
                        {category.scopes.map((s) => s.name).join(" · ")}
                      </p>
                    )}
                  </div>
                );
              })}
              {(categories ?? []).length === 0 && (
                <p className="text-sm text-[var(--color-text-muted)]">
                  Słownik kategorii jest pusty — uzupełnij go w{" "}
                  <Link to="/rfq/categories" className="underline hover:text-[var(--color-text-primary)]">
                    RFQ → „Kategorie i zakresy”
                  </Link>
                  .
                </p>
              )}
            </div>
          ) : qualification.categories.length ? (
            <div className="space-y-2">
              {qualification.categories.map((c) => (
                <div key={c.id}>
                  <Badge color={c.status === "conditional" ? "yellow" : "green"}>
                    {c.category_name}
                    {c.status === "conditional" && " — warunkowa"}
                  </Badge>
                  {!c.all_positions && (
                    <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                      tylko: {c.positions.map((p) => p.name).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">
              Brak przyznanych kategorii — firma nie wystartuje w żadnym postępowaniu.
            </p>
          )}
        </div>

        {/* Województwa */}
        <div className="bg-white p-5 dark:bg-gray-800">
          <p className="mb-3 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
            <MapPin size={12} /> Obszar działania
          </p>
          {editing ? (
            /* Jedna kolumna: rozwinięta lista powiatów potrzebuje całej szerokości panelu,
               w połówce nazwy powiatów nachodziły na sąsiednie województwo. */
            <div className="space-y-1">
              {(voivodeships ?? []).map((v) => (
                <div key={v.value}>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={areas.includes(v.value)}
                      onChange={() => toggleArea(v.value)}
                    />
                    <span className="text-[var(--color-text-primary)]">{v.label}</span>
                  </label>
                  {/* Powiaty pod zaznaczonym województwem — zwinięte, rozwijane do doprecyzowania. */}
                  {areas.includes(v.value) && (
                    <CountyRefinement
                      option={v}
                      selected={selectedCounties(v, counties)}
                      onChange={(next) => setCounties((prev) => withCounties(v, prev, next))}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : qualification.voivodeships.length ? (
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {qualification.voivodeships.map((v) => (
                <span key={v.id} className="inline-flex items-center gap-1.5">
                  <Badge color="blue">{v.voivodeship_label}</Badge>
                  <CountyDisclosure
                    names={v.counties_detail.map((c) => c.name)}
                    total={v.county_total}
                  />
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">Brak wskazanych województw.</p>
          )}
        </div>
      </div>
    </section>
  );
}
